import { useQuery } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { apiClient, responseData } from "@/api/client";
import type { BankAccountOption, Domain, ReconciliationMaster } from "@/api/types";
import { useWorkspace } from "@/app/workspace";
import { Breadcrumb, HomeIcon } from "@/components/Breadcrumb";

const pageKeys = ["page", "movementPage", "ledgerPage", "groupPage", "reviewGroupPage", "reviewIssuePage", "reviewAutomaticPage", "reviewCriteriaPage", "rowPage", "issuePage"];

const reconciliationModules = [
  { domain: "BANK", label: "Bancos", to: "/bancos" },
  { domain: "CARD", label: "Tarjetas", to: "/tarjetas" },
  { domain: "OTA", label: "Cobranzas OTA", to: "/cobranzas-ota" },
] as const;

export const masterSearch = (masterId: string) => masterId ? `?masterId=${encodeURIComponent(masterId)}` : "";
export const bankSearch = (bankId: string, currency: string) => {
  const params = new URLSearchParams();
  if (bankId) params.set("bankId", bankId);
  if (currency) params.set("currency", currency);
  const value = params.toString();
  return value ? `?${value}` : "";
};

function MoreIcon() {
  return <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><circle cx="5" cy="12" r="1.5" /><circle cx="12" cy="12" r="1.5" /><circle cx="19" cy="12" r="1.5" /></svg>;
}

function ContextActionMenu({ children, moduleName }: { children: ReactNode; moduleName: string }) {
  const [open, setOpen] = useState(false);
  const menuId = useId();
  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      buttonRef.current?.focus();
    };
    const closeOnPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("keydown", closeOnEscape);
    document.addEventListener("pointerdown", closeOnPointerDown);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      document.removeEventListener("pointerdown", closeOnPointerDown);
    };
  }, [open]);

  return <div className="domain-context-card__menu" ref={containerRef}>
    <button
      ref={buttonRef}
      type="button"
      className="context-kebab"
      aria-label={`Abrir acciones de ${moduleName}`}
      aria-controls={menuId}
      aria-expanded={open}
      onClick={() => setOpen((value) => !value)}
    >
      <MoreIcon />
    </button>
    <div id={menuId} className="context-action-menu" aria-label={`Acciones de ${moduleName}`} hidden={!open} onClick={(event) => {
      if (event.target instanceof Element && event.target.closest("a, button")) setOpen(false);
    }}>
      {children}
    </div>
  </div>;
}

const completeIsoDate = /^\d{4}-\d{2}-\d{2}$/;

function DateRangeField({
  field,
  label,
  value,
  onChange,
}: {
  field: "from" | "to";
  label: string;
  value: string;
  onChange(field: "from" | "to", value: string): void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const committedValue = useRef(value);

  useEffect(() => {
    committedValue.current = value;
    if (
      input.current &&
      document.activeElement !== input.current &&
      input.current.value !== value
    ) {
      input.current.value = value;
    }
  }, [value]);

  const commit = (nextValue: string) => {
    if (nextValue === committedValue.current) return;
    committedValue.current = nextValue;
    onChange(field, nextValue);
  };

  return (
    <label>
      <span>{label}</span>
      <input
        ref={input}
        aria-label={`Período ${label.toLowerCase()}`}
        type="date"
        defaultValue={value}
        onChange={(event) => {
          const nextValue = event.currentTarget.value;
          if (completeIsoDate.test(nextValue)) commit(nextValue);
        }}
        onBlur={(event) => {
          if (!event.currentTarget.value) commit("");
        }}
      />
    </label>
  );
}

export function DomainContext({
  domain,
  moduleName,
  actions,
  children,
  compactFilters = false,
  compactContext = false,
  dateRange,
  hidePeriod = false,
  hideMaster = false,
  filterAddon,
}: {
  domain: Domain;
  moduleName: string;
  actions?: ReactNode;
  children: ReactNode;
  compactFilters?: boolean;
  compactContext?: boolean;
  dateRange?: {
    from: string;
    to: string;
    onChange(field: "from" | "to", value: string): void;
  };
  hidePeriod?: boolean;
  hideMaster?: boolean;
  filterAddon?: ReactNode;
}) {
  const { hotel, hotels, hotelId, period, setHotelId, setPeriod } = useWorkspace();
  const [changingHotel, setChangingHotel] = useState(false);
  const [params, setParams] = useSearchParams();
  const masterId = params.get("masterId") ?? "";
  const bankId = params.get("bankId") ?? (domain === "BANK" ? masterId : "");
  const currency = params.get("currency") ?? "";
  const masters = useQuery({
    queryKey: ["reconciliation-master-options", domain],
    queryFn: async () => {
      if (domain === "CARD") {
        const result = await responseData(apiClient.GET("/api/v1/cards/catalog"));
        return result.map((item): ReconciliationMaster => ({ ...item, domain: "CARD", aliases: [], sortOrder: 0 }));
      }
      const result = await responseData(apiClient.GET("/api/v1/ota/agencies", {
        params: { query: { pageSize: 100, active: "true" } },
      }));
      return result.items.map((item): ReconciliationMaster => ({ ...item, domain: "OTA", aliases: item.aliases, sortOrder: 0, reportProfile: item.reportProfile ?? "" }));
    },
    enabled: !hideMaster && domain !== "BANK",
  });
  const bankAccounts = useQuery({
    queryKey: ["bank-account-options", hotelId, period],
    queryFn: (): Promise<BankAccountOption[]> => responseData(apiClient.GET("/api/v1/banking/accounts", {
      params: { query: { hotelId, period } },
    })),
    enabled: !hideMaster && domain === "BANK" && Boolean(hotelId),
  });
  const update = (key: "masterId" | "bankId" | "currency" | "period", value: string) => {
    if (key === "period") setPeriod(value);
    const next = new URLSearchParams(params);
    if (key === "masterId") value ? next.set(key, value) : next.delete(key);
    if (key === "bankId") {
      value ? next.set(key, value) : next.delete(key);
      next.delete("masterId");
      const compatible = bankAccounts.data?.some((account) => account.bankId === value && account.currency === next.get("currency"));
      if (value && !compatible) next.delete("currency");
    }
    if (key === "currency") value ? next.set(key, value) : next.delete(key);
    pageKeys.forEach((pageKey) => next.delete(pageKey));
    setParams(next, { replace: true });
  };
  const selectHotel = (nextHotelId: string) => {
    if (!nextHotelId) return;
    setHotelId(nextHotelId);
    setChangingHotel(false);
  };
  const entityLabel = domain === "BANK" ? "Banco" : domain === "CARD" ? "Tarjeta" : "OTA / Agencia";
  const accountOptions = Array.isArray(bankAccounts.data) ? bankAccounts.data : [];
  const bankOptions = [...new Map(accountOptions.map((account) => [account.bankId, { id: account.bankId, name: account.bankName }])).values()];
  const scopedAccounts = accountOptions.filter((account) => !bankId || account.bankId === bankId);
  const currencyOptions = [...new Set(scopedAccounts.map((account) => account.currency))].map((item) => ({ currency: item, active: scopedAccounts.some((account) => account.currency === item && account.active) }));
  const contextCard = <section className={`domain-context-card${compactFilters ? " domain-context-card--compact-filters" : ""}${compactContext ? " domain-context-card--compact-context" : ""}${dateRange ? " domain-context-card--date-range" : ""}${hidePeriod && hideMaster ? " domain-context-card--hotel-only" : ""}${filterAddon ? " domain-context-card--with-filter-action" : ""}`} aria-label={`Contexto de ${moduleName}`}>
    <div className="domain-context-card__hotel">
      {hotel?.logo ? <img src={`/${hotel.logo}`} alt="" /> : <span className="domain-context-card__logo-fallback"><HomeIcon /></span>}
      {changingHotel ? (
        <label className="domain-context-card__hotel-select">
          <span>Hotel</span>
          <select aria-label="Seleccionar hotel" value={hotelId} onChange={(event) => selectHotel(event.target.value)} autoFocus>
            {hotels.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </select>
        </label>
      ) : <span><small>Hotel</small><strong>{hotel?.name ?? "Cargando"}</strong></span>}
      <button type="button" className="domain-context-card__change" onClick={() => setChangingHotel((value) => !value)}>{changingHotel ? "Cancelar" : "Cambiar"}</button>
      {actions && (compactContext ? <ContextActionMenu moduleName={moduleName}>{actions}</ContextActionMenu> : <div className="domain-context-card__actions">{actions}</div>)}
    </div>
    {dateRange ? <>
      <DateRangeField field="from" label="Desde" value={dateRange.from} onChange={dateRange.onChange} />
      <DateRangeField field="to" label="Hasta" value={dateRange.to} onChange={dateRange.onChange} />
    </> : !hidePeriod && <label><span>{"Per\u00edodo"}</span><input type="month" value={period} onChange={(event) => update("period", event.target.value)} /></label>}
    {!hideMaster && domain === "BANK" && <><label><span>Banco</span><select value={bankId} onChange={(event) => update("bankId", event.target.value)}>
      <option value="">Todos los bancos</option>
      {bankOptions.map((bank) => <option key={bank.id} value={bank.id}>{bank.name}</option>)}
    </select></label><label><span>Cuenta</span><select value={currency} onChange={(event) => update("currency", event.target.value)}>
      <option value="">Todas las monedas</option>
      {currencyOptions.map((item) => <option key={item.currency} value={item.currency}>{item.currency === "ARS" ? "Pesos (ARS)" : "Dólares (USD)"}{item.active ? "" : " · Inactiva"}</option>)}
    </select></label></>}
    {!hideMaster && domain !== "BANK" && <div className="domain-context-card__master"><label><span>Filtrar tablas por {entityLabel.toLowerCase()}</span><select value={masterId} onChange={(event) => update("masterId", event.target.value)}>
      <option value="">Todos</option>
      {masters.data?.map((master) => <option key={master.id} value={master.id}>{master.name}</option>)}
    </select></label>{filterAddon}</div>}
  </section>;

  return <>
    <Breadcrumb
      items={[
        { label: `Hotel ${hotel?.name ?? "seleccionado"}` },
        {
          label: moduleName,
          selectorLabel: `Cambiar módulo. Actual: ${moduleName}`,
          options: reconciliationModules
            .filter((item) => item.domain !== domain)
            .map(({ label, to }) => ({ label, to })),
        },
      ]}
    />
    {compactContext ? <div className="domain-header-with-context">{children}{contextCard}</div> : <>{children}{contextCard}</>}
  </>;
}
